import { PrivacyPolicy } from './privacy-policy';
import { Router } from '@angular/router';
import { SnackBarService } from './../core/snack-bar.service';
import { SiteContextService } from './../core/site-context.service';
import { PrivacyPolicyType, Widget } from './widget';
import { Layout } from './layout';
import { Injectable } from '@angular/core';
import { TriggerbeeApi, TriggerbeeRequester } from './../core/triggerbee/triggerbee-requester.service';
import { Logger, LoggerFactory } from './../core/logger.service';
import { firstValueFrom, Observable } from 'rxjs';
import { HttpRequestDecorator } from '../core/triggerbee/http.request.decorator';

@Injectable()
export class ConsentService {
  private readonly logger: Logger;

  constructor(
    loggerFactory: LoggerFactory,
    private readonly requester: TriggerbeeRequester,
    protected readonly router: Router,
    private readonly siteContext: SiteContextService,
    private http: HttpRequestDecorator,
    private readonly snackbar: SnackBarService,
  ) {
    this.logger = loggerFactory.get('ConsentService');
  }

  // Creates or updates the widget, returns updated widget on success and undefined on fail
  public async createOrUpdateWidgetAsync(model: PrivacyPolicy): Promise<PrivacyPolicy> {
    const response = await this.requester.postAsync<PrivacyPolicy>(
      TriggerbeeApi.Gateway,
      '/consent/privacy-policy',
      model,
    );
    if (!response.isPositive()) {
      if (response.error.indexOf('Invalid argument') >= 0) {
        const message = response.error.replace('Invalid argument', 'Could not save widget, Please fill required field');
        this.snackbar.error(message);
      } else {
        this.snackbar.error('consent.editWidget.publishError');
      }
      this.logger.error(`Creating or updating widget has failed. Response: ${response.toString()}`);
      return undefined;
    }
    return response.data;
  }

  public async publishWidgetAsync(model: Widget): Promise<Widget> {
    const response = await this.requester.postAsync<Widget>(TriggerbeeApi.Gateway, '/consent/publish', model);
    return response.data;
  }

  // No-op: publishWidgetAsync now handles publish + client script update via Gateway
  public async publishAsync() {}

  public async getWidgetAsync(siteId: number): Promise<Widget> {
    const response = await this.requester.getAsync<Widget>(TriggerbeeApi.Gateway, '/consent/widget/' + siteId);
    if (response.isNotFound()) {
      this.logger.info(`Widget does not exist with id ${siteId}`);
      return undefined;
    }
    if (!response.isPositive()) {
      this.logger.error(`Widget could not be get : ${response.toString()}`);
      return undefined;
    }
    return response.data;
  }

  public async getPrivacyPolicyAsync(siteId: number, type: PrivacyPolicyType): Promise<PrivacyPolicy> {
    const response = await this.requester.getAsync<PrivacyPolicy>(
      TriggerbeeApi.Gateway,
      '/consent/privacy-policy/' + siteId,
      { type: PrivacyPolicyType[type] },
    );
    if (response.isNotFound()) {
      this.logger.info(`PrivacyPolicy does not exist with id ${siteId}`);
      return null;
    }
    if (!response.isPositive()) {
      this.logger.error(`PrivacyPolicy could not be get : ${response.toString()}`);
      return null;
    }
    return response.data;
  }

  public async getUrlAsync(siteId: number): Promise<string> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + '/consent/widget/url';
    return await firstValueFrom(this.http.getDownload(url, { siteId: siteId }));
  }

  public async getLayoutsAsync(): Promise<Layout[]> {
    const response = await this.requester.getAsync<Layout[]>(TriggerbeeApi.Gateway, '/consent/layouts');
    if (!response.isPositive()) {
      this.logger.error(`Layouts could not be get : ${response.toString()}`);
      return null;
    }
    return response.data;
  }

  public async hasConsentAccountAsync(): Promise<boolean> {
    // TODO: Move to ConsentSettingsService
    // Checking current site first is important as getCurrentSiteAsync() locks the synchronization context until the
    // sites are initialized.
    const currentSite = await this.siteContext.getCurrentSiteAsync();
    if (!currentSite) {
      this.logger.info('User has no current site, therefore no consent account');
      return false;
    }
    const response = await this.requester.getAsync<boolean>(TriggerbeeApi.Gateway, '/consent/accounts/exists');
    if (!response.isPositive()) {
      if (response.isNotFound()) {
        this.logger.debug('The user has no consent account');
        return false;
      }
      this.logger.error(`hasConsentAccount failed : ${response.toString()}`);
      if (response.statusCode === 0) {
        this.logger.error('Could not establish request to Gateway');
        this.snackbar.error();
        this.router.navigate(['/error'], {
          queryParams: { path: window.location.pathname },
        });
      }
    }
    return response.data;
  }

  public async toggleActiveAsync(widget: Widget, active: boolean): Promise<Widget> {
    const response = await this.requester.putAsync<Widget>(TriggerbeeApi.Gateway, '/consent/status/toggle', {
      active: active,
    });
    if (!response.isPositive()) {
      this.logger.error(`toggleActive failed : ${response.toString()}`);
      return null;
    }
    return response.data;
  }

  // No-op: toggleActiveAsync now handles toggle + client script update via Gateway
  public async statusToggle(): Promise<boolean> {
    return true;
  }

  public getIsConsentWidgetIsActive(siteId: number): Observable<any> {
    const url = this.http.getBaseUrl(TriggerbeeApi.Gateway) + `/consent/${siteId}/is-active`;
    return this.http.get(url);
  }
}
